import { useState, useEffect } from "react";
import { listAllReports } from "@/services/api/report-builder";
import type { Report as ApiReport } from "@/services/api/report-builder/types";
import { Button } from "@/components/ui/button/Button";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChartColumn,
  faDownload,
  faEye,
  faScreenUsers,
} from "@awesome.me/kit-70c1f0055f/icons/classic/light";
import { Spinner } from "@/components/ui/spinner";
import { useOsActionsOptional } from "@/components/ui/os-layout/OsContext";
import { tryOpenUrlInOsMode } from "@/components/ui/os-layout/openUrlInOsMode";
import {
  GenerateConfirmDialog,
  type GenerateConfirmResult,
} from "@/components/reports/GenerateConfirmDialog";
import {
  generateSingleReportPDF,
  type SingleGenerateExportType,
} from "@/routes/reportsSingleGenerateDownload";
import {
  applyFilenameTemplate,
  formatDateForFilename,
} from "@/utils/formatters";
import { defaultAsAtDateForFilename } from "@/utils/reportAsAtDate";

export interface EntityReportsListProps {
  entityId: string;
  entityType?: "portfolio" | "product";
  entityName?: string;
}

type PendingReportAction = SingleGenerateExportType;

export const EntityReportsList: React.FC<EntityReportsListProps> = ({
  entityId,
  entityType = "product",
  entityName,
}) => {
  const osActions = useOsActionsOptional();
  const [reports, setReports] = useState<ApiReport[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [exportingReportId, setExportingReportId] = useState<string | null>(
    null,
  );
  const [selectedReport, setSelectedReport] = useState<ApiReport | null>(null);
  const [pendingAction, setPendingAction] =
    useState<PendingReportAction>("download");
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);

  useEffect(() => {
    const fetchReports = async () => {
      try {
        setLoading(true);
        const reportList = await listAllReports();
        if (Array.isArray(reportList)) {
          const compatibleReports = reportList.filter((report) => {
            const resourceSlots = report.data?.resource_slots || [];
            return resourceSlots.some((slot) =>
              slot.supported_types?.includes(entityType),
            );
          });
          setReports(compatibleReports);
        } else {
          console.error("listAllReports did not return an array:", reportList);
          setReports([]);
        }
      } catch (err) {
        setError(
          err instanceof Error ? err.message : "Failed to fetch reports",
        );
      } finally {
        setLoading(false);
      }
    };

    fetchReports();
  }, [entityType]);

  const openConfirmDialog = (
    report: ApiReport,
    action: PendingReportAction,
  ) => {
    setSelectedReport(report);
    setPendingAction(action);
    setConfirmDialogOpen(true);
  };

  const closeConfirmDialog = () => {
    setConfirmDialogOpen(false);
    setSelectedReport(null);
  };

  const handleConfirm = async (result: GenerateConfirmResult) => {
    if (!selectedReport) return;

    const report = selectedReport;
    closeConfirmDialog();

    const resources = [
      {
        id: entityId,
        type: entityType,
        name: entityName,
      },
    ];

    if (pendingAction === "download") {
      setExportingReportId(report._id);
    }

    const openInOs = (url: string) =>
      tryOpenUrlInOsMode(osActions?.openWindow, url, {
        title: report.name || "Report",
      });

    try {
      const filename =
        pendingAction === "download"
          ? applyFilenameTemplate(result.filenameTemplate, {
              report_name: report.name,
              report_type: report.type,
              resource_name: entityName || entityId,
              date: formatDateForFilename(
                result.asAtDate || defaultAsAtDateForFilename(),
              ),
            }) + ".pdf"
          : undefined;

      await generateSingleReportPDF(
        report,
        resources,
        result.asAtDate,
        pendingAction,
        filename,
        openInOs,
      );
    } catch (err) {
      console.error(`Failed to ${pendingAction} report:`, err);
    } finally {
      if (pendingAction === "download") {
        setExportingReportId(null);
      }
    }
  };

  const showDatePicker =
    selectedReport?.data?.config_options?.as_at_date !== false;

  const isViewAction =
    pendingAction === "view" || pendingAction === "view-static";

  const confirmTitle =
    pendingAction === "view"
      ? "Slideshow"
      : pendingAction === "view-static"
        ? "View Report"
        : "Generate Report";

  const confirmButtonText =
    pendingAction === "view"
      ? "Slideshow"
      : pendingAction === "view-static"
        ? "View"
        : "Generate";

  if (loading) {
    return <div className="p-4">Loading reports...</div>;
  }

  if (error) {
    return <div className="p-4 text-red-500">Error: {error}</div>;
  }

  if (reports.length === 0) {
    return (
      <div className="p-6 text-center">
        <FontAwesomeIcon
          icon={faChartColumn}
          className="w-12 h-12 mx-auto mb-4 text-slate-400 dark:text-slate-500"
          aria-hidden
        />
        <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-2">
          No Compatible Reports
        </h3>
        <p className="text-slate-600 dark:text-slate-300 max-w-md mx-auto">
          No reports are available for {entityType}s. Reports need to have{" "}
          {entityType} as a supported resource type to appear here.
        </p>
      </div>
    );
  }

  return (
    <div className="p-4" data-testid="entity-reports-list">
      <div className="mb-4">
        <h3 className="text-lg font-semibold text-slate-900 dark:text-white">
          Available Reports for {entityName || `this ${entityType}`}
        </h3>
        <p className="text-sm text-slate-600 dark:text-slate-300">
          Generate reports using current data from this {entityType}
        </p>
      </div>

      <div className="space-y-3">
        {reports.map((report) => (
          <div
            key={report._id}
            className="flex items-center justify-between gap-4 p-4 bg-slate-50 dark:bg-slate-800 rounded-lg"
            data-testid={`entity-report-row-${report._id}`}
          >
            <h4 className="font-medium text-slate-900 dark:text-white min-w-0 flex-1">
              {report.name}
            </h4>

            <div className="flex items-center gap-2 shrink-0">
              <Button
                color="slate"
                className="whitespace-nowrap"
                data-testid={`entity-report-view-${report._id}`}
                dropdown
                dropdownItems={[
                  {
                    name: "Page View",
                    icon: <FontAwesomeIcon icon={faEye} className="w-4" />,
                    onClick: () => openConfirmDialog(report, "view-static"),
                  },
                  {
                    name: "Slideshow",
                    icon: (
                      <FontAwesomeIcon icon={faScreenUsers} className="w-4" />
                    ),
                    onClick: () => openConfirmDialog(report, "view"),
                  },
                ]}
              >
                View
              </Button>

              <Button
                onClick={() => openConfirmDialog(report, "download")}
                icon={<FontAwesomeIcon icon={faDownload} />}
                disabled={exportingReportId === report._id}
                color="indigo"
                data-testid={`entity-report-generate-${report._id}`}
              >
                {exportingReportId === report._id ? (
                  <Spinner className="w-4 h-4" />
                ) : (
                  "Generate"
                )}
              </Button>
            </div>
          </div>
        ))}
      </div>

      {selectedReport && (
        <GenerateConfirmDialog
          open={confirmDialogOpen}
          onClose={closeConfirmDialog}
          onConfirm={handleConfirm}
          report={{
            name: selectedReport.name,
            type: selectedReport.type,
          }}
          sampleResourceName={entityName || entityId}
          showDatePicker={showDatePicker}
          showFilenameTemplate={!isViewAction}
          title={confirmTitle}
          confirmButtonText={confirmButtonText}
        />
      )}
    </div>
  );
};
